/**
 * The press coverage the backend collects per company
 * (json_data.basic_company_details.company_news), shared by the IPO detail
 * page and the DRHP filing page.
 *
 * The backend's news search matches on single words, so roughly one story in
 * six comes back about something else entirely -- "Absolute Projects India"
 * returns James Bond and a web host, "German Green Steel and Power" returns
 * Porsche at the Nürburgring. Everything here is about keeping those out.
 */
const NAME_NOISE = /^(?:the|and|of|for|limited|ltd|private|pvt|company|india|indian)$/i;

const text = (value) => (typeof value === 'string' && value.trim() ? value.trim().replace(/\s+/g, ' ') : null);

const URL_LIKE = /^(?:https?:\/\/|www\.)[\w-]+(?:\.[\w-]+)+/i;
const newsUrl = (value) => {
  const flat = text(value)?.replace(/[,;]+$/, '');
  if (!flat || !URL_LIKE.test(flat)) return null;
  return /^https?:\/\//i.test(flat) ? flat : `https://${flat}`;
};

/**
 * Consecutive word pairs from a company name ("german green", "green steel",
 * "steel power"), which is what a story has to contain to be about it.
 *
 * A single shared word is not evidence; two adjacent ones are.
 */
export const companyNamePhrases = (company) => {
  const words = String(company || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2 && !NAME_NOISE.test(word));
  if (words.length < 2) return words;
  return words.slice(0, -1).map((word, index) => `${word} ${words[index + 1]}`);
};

/** @param {object} basic - json_data.basic_company_details */
export const buildCompanyNews = (basic = {}, company = '', limit = 6) => {
  const phrases = companyNamePhrases(company || basic.company_name);
  return (Array.isArray(basic.company_news) ? basic.company_news : [])
    .map((item) => ({
      title: text(item?.title),
      url: newsUrl(item?.url),
      source: text(item?.source),
      body: text(item?.body),
      date: text(item?.date),
    }))
    .filter((item) => item.title && item.url)
    .filter((item) => {
      if (!phrases.length) return true;
      const haystack = `${item.title} ${item.body || ''}`.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ');
      return phrases.some((phrase) => haystack.includes(phrase));
    })
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
    .slice(0, limit);
};

/** "2026-05-17T18:30:00+00:00" -> "17 May 2026", or null when unparseable */
export const formatNewsDate = (value) => {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "MSN · via Yahoo News" -> "MSN" */
export const newsSourceLabel = (source) => text(source)?.split(/\s+·\s+|\s+\|\s+/)[0] || null;
